import { faEnvelope, faLock } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import type { GraphQLError } from "graphql";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SharedButton } from "components/shared/Buttons/SharedButton";
import { TextInput } from "components/shared/Inputs/TextInput";
import { Typography } from "components/shared/Typography";
import type { ExternalPageTypeEnum } from "generated/graphqlExternal";

import { apiTranslate } from "@/translations/i18n";
import ExternalPageCard from "../ExternalPageCard";
import { useStyles } from "./styles";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;
const BLOCKING_ERROR_CODES = ["OTP_EXPIRED", "OTP_TOO_MANY_ATTEMPTS"];

interface IOtpFormProps {
  email: string;
  inviteType: ExternalPageTypeEnum;
  loading: boolean;
  resending: boolean;
  error: (GraphQLError & { code: string }) | null;
  onSubmit: (code: string) => void;
  onResend: () => void;
}

const formatCooldown = (seconds: number): string => `0:${seconds.toString().padStart(2, "0")}`;

const OtpForm: React.FC<IOtpFormProps> = ({ email, inviteType, loading, resending, error, onSubmit, onResend }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [cooldownSeconds, setCooldownSeconds] = useState(RESEND_COOLDOWN_SECONDS);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (cooldownSeconds <= 0) {
      return undefined;
    }

    const timer = setInterval(() => setCooldownSeconds(seconds => seconds - 1), 1000);

    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const isBlocked = Boolean(error && BLOCKING_ERROR_CODES.includes(error.code));
  const isComplete = digits.every(digit => digit !== "");

  const handleChange = (index: number, value: string): void => {
    const digit = value.replace(/\D/g, "").slice(-1);

    setDigits(previous => previous.map((current, currentIndex) => (currentIndex === index ? digit : current)));

    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Backspace" && digits[index] === "" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setDigits(previous => previous.map((current, currentIndex) => (currentIndex === index - 1 ? "" : current)));
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>): void => {
    event.preventDefault();

    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH).split("");

    if (pasted.length === 0) {
      return;
    }

    setDigits(Array.from({ length: CODE_LENGTH }, (_, index) => pasted[index] ?? ""));
    inputRefs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    onSubmit(digits.join(""));
  };

  const handleResend = (): void => {
    setDigits(Array(CODE_LENGTH).fill(""));
    setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    inputRefs.current[0]?.focus();
    onResend();
  };

  const title = t("external_invite.otp_form.title");

  return (
    <ExternalPageCard
      pageTitle={title}
      headerTitle={apiTranslate(`enums.external_page_type_enum.${inviteType}`)}
      dataCy="externalInviteOtpForm"
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        className={classes.form}
      >
        <Box className={classes.iconCircle}>
          <FontAwesomeIcon icon={faEnvelope} />
        </Box>

        <Typography className={classes.title}>{title}</Typography>

        <Typography className={classes.description}>{t("external_invite.otp_form.description", { email })}</Typography>

        <Box className={classes.digitsRow}>
          {digits.map((digit, index) => (
            <TextInput
              key={index}
              inputRef={(element: HTMLInputElement | null) => {
                inputRefs.current[index] = element;
              }}
              value={digit}
              error={Boolean(error)}
              disabled={isBlocked || loading}
              onChange={event => handleChange(index, event.target.value)}
              onKeyDown={event => handleKeyDown(index, event)}
              onPaste={handlePaste}
              className={classes.digitInput}
              slotProps={{
                htmlInput: {
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 1,
                  "data-cy": `externalInviteOtpDigit${index}`,
                  className: classes.digitInputBase,
                },
              }}
            />
          ))}
        </Box>

        {error ? (
          <Typography
            className={classes.errorMessage}
            data-cy="externalInviteOtpError"
          >
            {error.message}
          </Typography>
        ) : (
          <Typography className={classes.hint}>{t("external_invite.otp_form.code_expiry_hint")}</Typography>
        )}

        <SharedButton
          type="submit"
          variant="contained"
          color="primary"
          loading={loading}
          disabled={!isComplete || isBlocked}
          className={classes.submitButton}
          data-cy="externalInviteOtpSubmit"
        >
          {error ? t("external_invite.otp_form.try_again") : t("external_invite.otp_form.submit")}
        </SharedButton>

        {cooldownSeconds > 0 ? (
          <Typography className={classes.hint}>
            {t("external_invite.otp_form.resend_cooldown", { time: formatCooldown(cooldownSeconds) })}
          </Typography>
        ) : (
          <SharedButton
            type="button"
            variant="text"
            onClick={handleResend}
            disabled={resending}
            className={classes.resendLink}
            data-cy="externalInviteOtpResend"
          >
            {t("external_invite.otp_form.resend_link")}
          </SharedButton>
        )}

        <Box className={classes.securityNotice}>
          <FontAwesomeIcon icon={faLock} />
          <Typography className={classes.securityNoticeText}>
            {t("external_invite.otp_form.security_notice")}
          </Typography>
        </Box>
      </Box>
    </ExternalPageCard>
  );
};

export default OtpForm;
